/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faCircleCheck, faCircleX, faGear, faQuestionCircle, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { faInputText, faRectangle } from '@fortawesome/pro-thin-svg-icons';
import { interval } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { SentenceTypeEnum } from 'src/modules/shared/models/enums/sentence-type.enum';
import { IRobotPreview, IRobotSentence } from 'src/modules/shared/models/interfaces/robots.interface';
import { ISmartCanvasOptions } from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import { RobotSentence } from 'src/modules/shared/models/robots.model';

@Component({
  selector: 'hcr-robot-creation-sentence-input',
  templateUrl: './robot-creation-sentence-input.component.html',
  styleUrls: ['./robot-creation-sentence-input.component.sass'],
})
export class RobotCreationSentenceInputComponent implements OnChanges, OnInit {
  @Input() sentence: RobotSentence;
  @Input() smartCanvasOptions: ISmartCanvasOptions;
  @Input() selectedRobotFilePreview!: IRobotPreview;
  @Output() onRemove: EventEmitter<void> = new EventEmitter();
  @Output() onChange: EventEmitter<IRobotSentence> = new EventEmitter();
  @Output() onChangeSliderValue: EventEmitter<number> = new EventEmitter();
  @Output() changeValue: EventEmitter<number> = new EventEmitter();
  @Output() mouseEnterSentenceEvent: EventEmitter<RobotSentence> = new EventEmitter();
  @Output() mouseLeaveSentenceEvent: EventEmitter<RobotSentence> = new EventEmitter();

  public icons = {
    gear: faGear,
    questionCircle: faQuestionCircle,
    trash: faTrash,
    check: faCheck,
    xMark: faXmark,
    circleCheck: faCircleCheck,
    circlex: faCircleX,
    inputPipe: faRectangle,
    inputText: faInputText,
  };

  public states: { [key: string]: boolean } = {
    configurationOpened: false,
    isValid: false,
  };

  public sentenceForm: FormGroup;
  public sentenceTypes = SentenceTypeEnum;

  public get thresholdValue(): number {
    return this.sentenceForm.controls['threshold'].value;
  }

  constructor() {
    this.sentenceForm = new FormGroup({
      text: new FormControl(''),
      regex: new FormControl(false),
      xPath: new FormControl(''),
      threshold: new FormControl(0.01),
      isDenied: new FormControl(false),
    });
  }

  public onMouseEnterSentence(): void {
    this.mouseEnterSentenceEvent.emit(this.sentence);
  }

  public onMouseLeaveSentence(): void {
    this.mouseLeaveSentenceEvent.emit(this.sentence);
  }

  public toggleConfiguration(): void {
    this.states.configurationOpened = !this.states.configurationOpened;
  }

  public remove(): void {
    this.onRemove.emit();
  }

  private _setOriginalValues(): void {
    this.sentenceForm.controls['text'].setValue(this.sentence.text || this.sentence.pattern);
    this.sentenceForm.controls['regex'].setValue(this.sentence.type === SentenceTypeEnum.Regex);
    this.sentenceForm.controls['threshold'].setValue(this.sentence.identificationThreshold);
    this.sentenceForm.controls['xPath'].setValue(this.sentence.xPath);
    this.sentenceForm.controls['isDenied'].setValue(this.sentence.isDenied);

    if (this.sentence.type === SentenceTypeEnum.XPath) {
      this.sentenceForm.controls['xPath'].setValidators([Validators.required]);
      this.sentenceForm.controls['text'].removeValidators([Validators.required]);
      this.sentenceForm.controls['threshold'].removeValidators([Validators.required]);
    } else {
      this.sentenceForm.controls['xPath'].removeValidators([Validators.required]);
      this.sentenceForm.controls['text'].setValidators([Validators.required]);
      this.sentenceForm.controls['threshold'].setValidators([Validators.required]);
    }
  }

  public changeThreshold(value: number): void {
    this.sentenceForm.controls['threshold'].setValue(value);
    this.changeValue.emit(value);
  }

  public emitChangeSliderValueEvent(value: number): void {
    this.onChangeSliderValue.emit(value);
  }

  private _checkValidity(): void {
    const validPages =
      this.selectedRobotFilePreview?.pages.filter(
        (page) =>
          page.sentences.find((stc) => {
            if (stc.sentence.type === SentenceTypeEnum.Default) {
              return stc.sentence.text === this.sentence.text;
            } else if (stc.sentence.type === SentenceTypeEnum.XPath) {
              return stc.sentence.xPath === this.sentence.xPath;
            } else {
              return stc.sentence.pattern === this.sentence.pattern;
            }
          }).isPassed
      ) || [];
    this.states.isValid = this.selectedRobotFilePreview ? validPages.length > 0 : true;
  }

  ngOnInit(): void {
    this.sentenceForm.valueChanges.pipe(debounce(() => interval(300))).subscribe(() => {
      this.onChange.emit({
        ...this.sentence,
        identificationThreshold: this.thresholdValue,
        text:
          this.sentence.type === SentenceTypeEnum.XPath
            ? this.sentenceForm.controls['xPath'].value
            : this.sentenceForm.controls['text'].value,
        xPath: this.sentenceForm.controls['xPath'].value,
        pattern: this.sentenceForm.controls['regex'].value ? this.sentenceForm.controls['text'].value : null,
        isDenied: this.sentenceForm.controls['isDenied'].value,
        type: this.sentenceForm.controls['regex'].value
          ? SentenceTypeEnum.Regex
          : this.sentenceForm.controls['xPath'].value
          ? SentenceTypeEnum.XPath
          : SentenceTypeEnum.Default,
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.sentence?.currentValue) {
      this._setOriginalValues();
      this._checkValidity();
    }

    if (changes.selectedRobotFilePreview?.currentValue) {
      this._checkValidity();
    }
  }
}
